import axios from "axios";
import type { ComponentType } from "react";
import type { IPublicClientApplication, AccountInfo } from "@azure/msal-browser";
import type {
  IMosaicInfo,
  ITileSettings,
  MapAccessToken,
  MapExplorerConfig,
  TileRequestTransformer,
} from "@spatio/map-explorer";
import { isValidExplorer } from "@spatio/stac-ui-kit";
import {
  isAuthRequest as hostIsAuthRequest,
  isGraphRequest as hostIsGraphRequest,
  requestToken,
} from "components/auth/helpers";
import {
  API_VERSION,
  ATLAS_DOMAIN,
  CONFIG_URL,
  DATA_URL,
  IMAGE_URL,
  STAC_URL,
} from "utils/constants";

interface HostConfigDeps {
  msalInstance: IPublicClientApplication;
  msalAccount: AccountInfo | null;
  azMapsClientId?: string;
  catalogSelector?: ComponentType;
}

// Builds a Spatio-shape MapExplorerConfig from the host's existing MSAL wiring, axios,
// and Spatio API constants. Mounted app-wide so library hooks (`useCollectionConfigManifest`
// etc.) resolve on catalog pages too; `azMapsClientId` starts empty and the provider
// re-renders once it resolves, gating map init inside <MapExplorer />.
export const buildHostMapExplorerConfig = ({
  msalInstance,
  msalAccount,
  azMapsClientId,
  catalogSelector,
}: HostConfigDeps): MapExplorerConfig => {
  const mosaicInfoProvider = async (
    collectionId: string | undefined
  ): Promise<IMosaicInfo> => {
    if (!collectionId) throw new Error("collectionId is required");
    const resp = await axios.get<{ mosaicInfo: IMosaicInfo }>(
      `${STAC_URL}/collections/${collectionId}/configurations`,
      { params: { "api-version": API_VERSION } }
    );
    return resp.data.mosaicInfo;
  };

  const tileSettingsProvider = async (
    collectionId: string | undefined
  ): Promise<ITileSettings | null> => {
    if (!collectionId) return null;
    try {
      const resp = await axios.get<ITileSettings>(
        `${STAC_URL}/collections/${collectionId}/configurations/tile-settings`,
        { params: { "api-version": API_VERSION } }
      );
      return resp.data;
    } catch (error) {
      // Only an unknown collection means "no settings"; auth and server errors must surface.
      if (axios.isAxiosError(error) && error.response?.status === 404) return null;
      throw error;
    }
  };

  // /configurations returns both sections in one payload; supply this so the library's
  // useCollectionConfigManifest hook doesn't fan out into two round-trips.
  const configManifestProvider = async (
    collectionId: string | undefined
  ): Promise<{ mosaicInfo: IMosaicInfo; tileSettings: ITileSettings | null }> => {
    if (!collectionId) throw new Error("collectionId is required");
    const resp = await axios.get<{
      mosaicInfo: IMosaicInfo;
      tileSettings: ITileSettings | null;
    }>(`${STAC_URL}/collections/${collectionId}/configurations`, {
      params: { "api-version": API_VERSION },
    });
    return {
      mosaicInfo: resp.data.mosaicInfo,
      tileSettings: resp.data.tileSettings ?? null,
    };
  };

  // Spatio mints atlas tokens server-side via CONFIG_URL/map/token; host axios already
  // carries the Entra bearer via the global RequestInterceptor.
  const mapTokenProvider = async (): Promise<MapAccessToken> => {
    const resp = await axios.get<{ token: string; expires_on: number }>(
      `${CONFIG_URL}/map/token`,
      { params: { "api-version": API_VERSION } }
    );
    return { token: resp.data.token, expiresOn: resp.data.expires_on };
  };

  const authTokenProvider = async (): Promise<string | null> => {
    try {
      const result = await requestToken(msalAccount, msalInstance);
      return result.accessToken;
    } catch {
      return null;
    }
  };

  // Bearer is added by the library's own transformer (authTokenProvider); atlas needs url+headers defined.
  const tileRequestTransformer: TileRequestTransformer = (url, resourceType) => {
    if (resourceType !== "Tile") return undefined;
    if (!url?.startsWith(DATA_URL)) return undefined;
    return { url, headers: {} };
  };

  return {
    stacApiUrl: STAC_URL,
    dataApiUrl: DATA_URL,
    imageApiUrl: IMAGE_URL,
    configApiUrl: CONFIG_URL,
    apiVersionQueryParam: API_VERSION,
    atlasDomain: ATLAS_DOMAIN,
    azMapsClientId: azMapsClientId ?? "",
    httpClient: axios,
    mapTokenProvider,
    authTokenProvider,
    isAuthRequest: hostIsAuthRequest,
    isGraphRequest: hostIsGraphRequest,
    enableTilePeriodicTokenRefresh: true,
    tileRequestTransformer,
    mosaicInfoProvider,
    tileSettingsProvider,
    configManifestProvider,
    itemEndpointStyle: "path",
    catalogSelector,
    collectionFilter: isValidExplorer,
    features: {
      animationExport: true,
      imageExport: true,
      exploreInHub: true,
    },
  };
};
